import { AxiosError } from 'axios';

import { JsonType } from '../typeguards';
import {
  HttpBadGateway,
  HttpBadMapping,
  HttpBadRequest,
  HttpException,
  HttpForbidden,
  HttpGatewayTimeout,
  HttpInternalServerError,
  HttpMethodNotAllowed,
  HttpNetworkError,
  HttpNotFound,
  HttpNotImplemented,
  HttpRequestTimeout,
  HttpServiceUnavailable,
  HttpUnauthorized,
  HttpUnprocessableEntity,
} from './http';
import { ServerErrorPayload } from './http/http-exception';

/**
 * Utility to map axios errors to http-exceptions and provide support
 * to add url to the exception message
 *
 * @param e - Error returned by axios
 * @param url - Url added to
 * @param unexpectedErrorStatusCode - Default status code if exception is not supported
 * @return HttpException or any supported concrete implementation
 */
const responsePayload = (error: AxiosError): ServerErrorPayload | undefined => {
  const data: JsonType | undefined = error.response?.data;
  if (data == null || data === '') return undefined;
  if (typeof data === 'string') return { type: 'text', content: data };
  return { type: 'json', content: data };
};

export const mapErrorToHttpException = ({
  error,
  url,
  unexpectedErrorStatusCode = 500,
  service,
}: {
  error: AxiosError;
  url?: string;
  unexpectedErrorStatusCode?: number;
  service?: string;
}): HttpException => {
  const { stack: stackTrace } = error;
  const simpleErrorBody = { url, previousError: error, stackTrace, service };
  const fullErrorBody = { ...simpleErrorBody, errorPayload: responsePayload(error) };

  if (error?.code === 'ECONNABORTED') {
    return new HttpRequestTimeout(simpleErrorBody);
  } else if (error?.code === 'ERR_NETWORK' || (error?.isAxiosError && error.response == null)) {
    // No HTTP response was received (client disconnect, offline, DNS/TLS/CORS failure, aborted
    // request). This is not a server error — do not default it to 500.
    return new HttpNetworkError(simpleErrorBody);
  } else if (error?.isAxiosError) {
    const status: number = error.response?.status ?? unexpectedErrorStatusCode;
    switch (status) {
      case 400:
        return new HttpBadRequest(fullErrorBody);
      case 401:
        return new HttpUnauthorized(fullErrorBody);
      case 403:
        return new HttpForbidden(fullErrorBody);
      case 404:
        return new HttpNotFound(fullErrorBody);
      case 421:
        return new HttpBadMapping(fullErrorBody);
      case 422:
        return new HttpUnprocessableEntity(fullErrorBody);
      case 405:
        return new HttpMethodNotAllowed(fullErrorBody);
      case 500:
        return new HttpInternalServerError(fullErrorBody);
      case 501:
        return new HttpNotImplemented(fullErrorBody);
      case 502:
        return new HttpBadGateway(fullErrorBody);
      case 503:
        return new HttpServiceUnavailable(fullErrorBody);
      case 504:
        return new HttpGatewayTimeout(fullErrorBody);
      default:
        return new HttpException({ status, ...fullErrorBody });
    }
  }
  // Fallback to
  return new HttpException({
    status: unexpectedErrorStatusCode,
    message: `Unexpected error: ${error.name ?? ''}: ${error.message ?? ''}`,
    ...fullErrorBody,
  });
};
